/** "Next containers": what's loading from a yard, how full it is, and joining or starting one.
 *  Every join and start waits for Dockie's yes; nothing is promised until the loader confirms. */
import React, { useState } from "react";
import { Check, Package, Plus, Truck, X } from "lucide-react";
import { useJoinContainer, useMyCars, useNextContainers, useStartContainer } from "@/services/python-api/customerHooks";
import { Card, Empty, Loading, Page, Pill, day } from "./parts";

const METHOD = {
  consolidated: { label: "Shared container", short: "Shared", tone: "bg-[#EFF6FF] text-[#1D4ED8]" },
  fcl: { label: "Full container", short: "Full box", tone: "bg-[#F3E8FF] text-[#6B21A8]" },
};

function Fill({ assigned, slots }) {
  return (
    <div className="flex gap-1.5" aria-label={`${assigned} of ${slots} cars in`}>
      {Array.from({ length: slots }, (_, i) => (
        <span key={i} className={`h-2.5 flex-1 rounded-full ${i < assigned ? "bg-[#2563EB]" : "bg-[#E4E7EC]"}`} />
      ))}
    </div>
  );
}

function CarPicker({ picked, setPicked }) {
  const { data } = useMyCars();
  const cars = (data?.cars ?? []).filter((c) => !c.confirming && c.stage !== "delivered" && c.stage !== "on_the_water");
  const toggle = (six) => setPicked((p) => (p.includes(six) ? p.filter((x) => x !== six) : [...p, six]));
  return (
    <div className="mt-2 max-h-56 space-y-1.5 overflow-auto">
      {cars.map((c) => (
        <button
          type="button"
          key={c.last6}
          onClick={() => toggle(c.last6)}
          className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left ${
            picked.includes(c.last6) ? "border-[#2563EB] bg-[#EFF6FF]" : "border-[#E4E7EC]"
          }`}
        >
          <span
            className={`flex h-5 w-5 items-center justify-center rounded-md border ${
              picked.includes(c.last6) ? "border-[#2563EB] bg-[#2563EB]" : "border-[#D0D5DD]"
            }`}
          >
            {picked.includes(c.last6) ? <Check size={14} color="white" /> : null}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px] font-medium text-[#101928]">{c.car}</span>
            <span className="block text-[12px] text-[#667185]">
              …{c.last6} · {c.stage_label || "Being confirmed"}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}

function Sheet({ title, sub, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 sm:items-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[480px] rounded-t-3xl bg-white p-6 shadow-xl sm:rounded-3xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-[18px] font-semibold text-[#101928]">{title}</h2>
            {sub ? <p className="mt-1 text-[14px] text-[#667185]">{sub}</p> : null}
          </div>
          <button onClick={onClose} aria-label="Close">
            <X size={20} color="#667185" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function YardToggle({ origin, on, setOn }) {
  return (
    <button
      type="button"
      onClick={() => setOn(!on)}
      className={`mt-3 flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left ${
        on ? "border-[#2563EB] bg-[#EFF6FF]" : "border-[#E4E7EC]"
      }`}
    >
      <Truck size={18} color={on ? "#2563EB" : "#667185"} />
      <span className="flex-1 text-[14px] text-[#101928]">Take my car to the {origin} yard for me</span>
      <span className={`h-5 w-9 rounded-full p-0.5 ${on ? "bg-[#2563EB]" : "bg-[#D0D5DD]"}`}>
        <span className={`block h-4 w-4 rounded-full bg-white transition ${on ? "translate-x-4" : ""}`} />
      </span>
    </button>
  );
}

function Sent({ onClose }) {
  return (
    <div className="mt-6 text-center">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#E7F6EC]">
        <Check size={24} color="#16A34A" />
      </span>
      <p className="mt-3 text-[16px] font-semibold text-[#101928]">Sent to Dockie</p>
      <p className="mt-1 text-[14px] text-[#667185]">
        We'll confirm your space once the loader confirms the plan. Nothing is charged until then.
      </p>
      <button onClick={onClose} className="mt-5 h-11 w-full rounded-full border border-[#D0D5DD] text-[15px] font-medium">
        Done
      </button>
    </div>
  );
}

function JoinSheet({ box, onClose }) {
  const join = useJoinContainer();
  const [picked, setPicked] = useState([]);
  const [yard, setYard] = useState(false);
  const error = join.error?.response?.data?.detail;
  return (
    <Sheet title={`Join the ${box.origin} container`} sub={`${box.free} of ${box.slots} spaces free · ${box.price || "price on request"}`} onClose={onClose}>
      {join.isSuccess ? (
        <Sent onClose={onClose} />
      ) : (
        <>
          <p className="mt-4 text-[13px] font-medium text-[#344054]">Which cars?</p>
          <CarPicker picked={picked} setPicked={setPicked} />
          <YardToggle origin={box.origin} on={yard} setOn={setYard} />
          {error ? <p className="mt-2 text-[13px] text-[#CB1A14]">{error}</p> : null}
          <button
            disabled={!picked.length || join.isPending}
            onClick={() => join.mutate({ id: box.id, cars: picked, to_the_yard: yard })}
            className="mt-5 h-11 w-full rounded-full bg-[#2563EB] text-[15px] font-medium text-white disabled:opacity-50"
          >
            Ask to join
          </button>
        </>
      )}
    </Sheet>
  );
}

function StartSheet({ origin, onClose }) {
  const start = useStartContainer();
  const [method, setMethod] = useState("consolidated");
  const [picked, setPicked] = useState([]);
  const [yard, setYard] = useState(true);
  const error = start.error?.response?.data?.detail;
  return (
    <Sheet title={`Start a container from ${origin}`} sub="Dockie quotes it and confirms the plan with the loader." onClose={onClose}>
      {start.isSuccess ? (
        <Sent onClose={onClose} />
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {[
              ["consolidated", "Shared", "Priced per car. Loads within 3 weeks of us having car and title."],
              ["fcl", "Full container", "The whole box is yours, priced as one. Usually loads within a week of car and title."],
            ].map(([key, label, what]) => (
              <button
                key={key}
                onClick={() => setMethod(key)}
                className={`rounded-2xl border p-3 text-left ${method === key ? "border-[#2563EB] bg-[#EFF6FF]" : "border-[#E4E7EC]"}`}
              >
                <span className="block text-[14px] font-semibold text-[#101928]">{label}</span>
                <span className="mt-0.5 block text-[12px] leading-snug text-[#667185]">{what}</span>
              </button>
            ))}
          </div>
          <p className="mt-4 text-[13px] font-medium text-[#344054]">Which cars?</p>
          <CarPicker picked={picked} setPicked={setPicked} />
          <YardToggle origin={origin} on={yard} setOn={setYard} />
          {error ? <p className="mt-2 text-[13px] text-[#CB1A14]">{error}</p> : null}
          <button
            disabled={!picked.length || start.isPending}
            onClick={() => start.mutate({ origin, method, cars: picked, to_the_yard: yard })}
            className="mt-5 h-11 w-full rounded-full bg-[#2563EB] text-[15px] font-medium text-white disabled:opacity-50"
          >
            Ask Dockie to start it
          </button>
        </>
      )}
    </Sheet>
  );
}

export default function NextContainers() {
  const [origin, setOrigin] = useState("Baltimore");
  const { data, isLoading } = useNextContainers(origin);
  const [joining, setJoining] = useState(null);
  const [starting, setStarting] = useState(false);
  const boxes = data?.containers ?? [];
  const origins = data?.origins ?? ["Houston", "Los Angeles", "Jacksonville/Savannah", "Baltimore"];
  return (
    <Page title="Next containers">
      <div className="-mx-1 mb-4 flex gap-2 overflow-x-auto px-1 pb-1">
        {origins.map((o) => (
          <button
            key={o}
            onClick={() => setOrigin(o)}
            className={`shrink-0 rounded-full border px-3.5 py-1.5 text-[14px] ${
              o === origin ? "border-[#2563EB] bg-[#2563EB] text-white" : "border-[#E4E7EC] bg-white text-[#344054]"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
      {isLoading ? (
        <Loading />
      ) : (
        <div className="space-y-3">
          {boxes.length === 0 ? (
            <Empty title={`No container planned from ${origin}`}>Start one, and we'll take your car to the {origin} yard.</Empty>
          ) : null}
          {boxes.map((box) => (
            <Card key={box.id}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="rounded-xl bg-[#F2F4F7] p-2.5">
                    <Package size={20} color="#344054" />
                  </span>
                  <div>
                    <p className="text-[15px] font-semibold text-[#101928]">
                      {box.size || "Container"} from {box.origin}
                    </p>
                    <p className="text-[13px] text-[#667185]">
                      {[box.line, box.to && `to ${box.to}`, box.made_by !== "Dockie" && `offered by ${box.made_by}`]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                </div>
                <Pill tone={METHOD[box.method].tone}>{METHOD[box.method].short}</Pill>
              </div>
              <div className="mt-4">
                <div className="mb-1.5 flex justify-between text-[13px]">
                  <span className="font-medium text-[#101928]">
                    {box.assigned} of {box.slots} cars in
                  </span>
                  <span className="text-[#667185]">{box.free} free</span>
                </div>
                <Fill assigned={box.assigned} slots={box.slots} />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 rounded-xl bg-[#F9FAFB] p-3 text-[13px]">
                <div>
                  <p className="text-[#667185]">Cut-off</p>
                  <p className="font-medium text-[#101928]">{day(box.cut_off) || "Not set"}</p>
                </div>
                <div>
                  <p className="text-[#667185]">Sails</p>
                  <p className="font-medium text-[#101928]">{day(box.sails) || "Not set"}</p>
                </div>
                <div>
                  <p className="text-[#667185]">Price</p>
                  <p className="font-medium text-[#101928]">{box.price || "On request"}</p>
                </div>
              </div>
              <p className="mt-3 text-[12px] text-[#667185]">Space is {box.capacity}.</p>
              {box.yours.length ? (
                <p className="mt-2 rounded-xl bg-[#FEF6E7] px-3 py-2 text-[13px] text-[#865503]">
                  Your {box.yours.flatMap((y) => y.cars.map((c) => `…${c}`)).join(", ")}:{" "}
                  {box.yours.every((y) => y.state === "accepted") ? "in this container" : "waiting for Dockie's yes"}
                </p>
              ) : box.method === "consolidated" && box.state === "open" ? (
                <button
                  onClick={() => setJoining(box)}
                  className="mt-3 h-11 w-full rounded-full bg-[#2563EB] text-[15px] font-medium text-white"
                >
                  Join this container
                </button>
              ) : null}
            </Card>
          ))}
          <button
            onClick={() => setStarting(true)}
            className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-[#D0D5DD] py-4 text-[15px] font-medium text-[#344054] hover:bg-[#F9FAFB]"
          >
            <Plus size={18} /> Start a container from {origin}
          </button>
        </div>
      )}
      {joining ? <JoinSheet box={joining} onClose={() => setJoining(null)} /> : null}
      {starting ? <StartSheet origin={origin} onClose={() => setStarting(false)} /> : null}
    </Page>
  );
}
